import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
  DestroyRef,
  ChangeDetectorRef,
  computed,
  effect,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { CommonModule } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { TranslateComponent } from '../../../core/components/translation/translation.component';
import { Store } from '@ngxs/store';
import {
  checkForNameErrors,
  getConsumerErrorText,
  getSubjectErrorText,
  MIN_NAME_LENGTH,
} from '../../../core/util';
import { FunctionalHelpDialogComponent } from '../../../core/components/functional-help-dialog/functional-help-dialog.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FunctionalHelpJsonModel } from '../../../functionalHelp/functionalHelp.json.model';
import { from, Observable, switchMap } from 'rxjs';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import { SubjectEntity } from '../../../subject/subject.entity';
import { SubjectEntityService } from '../../../subject/services/subject.entity.service';
import { ExerciseEntity } from '../exercise.entity';
import { ConsumerEntityService } from '../../../consumer/services/consumer.entity.service';
import { normalizeOneRelation } from '../../../core/decorators/relation.decorator';
import {
  getStatusColor,
  getStatusText,
  StateFilterCBComponent,
} from '../../../utils/component/state-filter-combobox/state-cb.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ObjectConsumerMetadataComponent } from '../../../consumer/object-consumer-metadata/object-consumer-metadata.component';
import { WidgetMessageService } from '../../../core/services/widget-message.service';
import { AssetEntity } from '../../../asset/asset.entity';

@Component({
  selector: 'pixl-add-exercise',
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatCardModule,
    MatIconModule,
    FormsModule,
    CommonModule,
    TranslateComponent,
    TranslocoModule,
    StateFilterCBComponent,
    ObjectConsumerMetadataComponent,
  ],
  templateUrl: './edit-exercise.component.html',
  styleUrl: './edit-exercise.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditExerciseComponent implements AfterViewInit, OnInit {
  private fb = inject(FormBuilder);
  private dialogRef = inject<MatDialogRef<EditExerciseComponent>>(MatDialogRef);
  data? = inject<Partial<ExerciseEntity>>(MAT_DIALOG_DATA);

  subjects$!: Observable<SubjectEntity[]>;
  currentPage = 1;
  pageSize = 20;

  private readonly store = inject(Store);
  private translocoService = inject(TranslocoService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private widgetService = inject(WidgetMessageService);
  private destroyRef = inject(DestroyRef);
  public cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  public formGroup: FormGroup;

  consumers$ = this.store.select(
    this.consumerService.allByQuery({ sort: 'name' }),
  );

  private consumers = toSignal(this.consumers$, { initialValue: [] });
  consumerId = signal<string | null>(null);
  consumer = computed<ConsumerEntity | undefined>(() => {
    const id = this.consumerId();
    const list = this.consumers();

    if (!id || list.length === 0) return undefined;

    return list.find((c) => c.id === id);
  });

  consumerTabName = computed(() => this.consumer()?.name ?? null);
  compareConsumer = (a: string | null, b: string | null) => a === b;

  public approval = signal(false);
  public assets: AssetEntity[] = [];
  public consumerError = signal(false);
  public subjectError = signal(false);
  public isNewRecord = signal(true);
  public consumerIsDeleted = signal(false);
  public competencyIsDeleted = signal(false);
  public key: string;

  constructor() {
    const data = this.data;

    effect(() => {
      const id = this.consumerId();
      const list = this.consumers();
      if (id && list.length) {
        const control = this.formGroup.controls.consumers;
        if (control.value !== id) {
          control.setValue(id, { emitEvent: false });
        }
      }
    });

    this.formGroup = this.fb.group({
      subjectId: new FormControl(
        { value: this.data?.subjectId || undefined, disabled: this.approval() }
      ),
      name: new FormControl(
        { value: this.data?.name || '', disabled: this.approval() },
        [Validators.required, Validators.minLength(MIN_NAME_LENGTH)],
      ),
      kind: new FormControl(
        { value: this.data?.kind || '', disabled: this.approval() },
        Validators.required,
      ),
      assignments: new FormControl({
        value: this.data?.assignments || [],
        disabled: this.approval(),
      }),
      assets: new FormControl({
        value: this.data?.assets || [],
        disabled: this.approval(),
      }),
      pages: new FormControl({
        value: this.data?.pages || [],
        disabled: this.approval(),
      }),
      correct: new FormControl({
        value: this.data?.correct || [],
        disabled: this.approval(),
      }),
      conditionalErrors: new FormControl({
        value: this.data?.conditionalErrors || [],
        disabled: this.approval(),
      }),
      quantityErrors: new FormControl({
        value: this.data?.quantityErrors || [],
        disabled: this.approval(),
      }),
      config: new FormControl({
        value: this.data?.config || [],
        disabled: this.approval(),
      }),
      approval: new FormControl({
        value: this.data?.approval || 'Edit',
        disabled: this.approval(),
      }),
      active: new FormControl(
        { value: this.data?.active || false, disabled: this.approval() },
        Validators.required,
      ),
      version: new FormControl({
        value: this.data?.version || 1,
        disabled: this.approval(),
      }),
      sortName: new FormControl(
        { value: this.data?.sortName || 'sortName', disabled: this.approval() },
        Validators.required,
      ),
      changedBy: new FormControl({
        value: this.data?.changedBy || '',
        disabled: this.approval(),
      }),
      consumerId: new FormControl(
        {
          value: this.consumerError() ? '' : this.data?.consumerId || undefined,
          disabled: this.approval(),
        }
      ),
      copyRestricted: new FormControl({
        value: this.data?.copyRestricted || false,
        disabled: this.approval(),
      }),
      consumers: [normalizeOneRelation(data?.consumers)],
      subjects: [normalizeOneRelation(data?.subjects)],
    });
    this.approval.set(this.data?.approval === 'Dormant');
    this.key = this.data?.kind || '';
  }

  public ngOnInit(): void {
    this.formGroup.markAllAsTouched();
    this.loadRelationData();

    const control = this.formGroup.controls.consumers;

    this.consumerId.set(control.value ?? null);

    control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        this.consumerId.set(id ?? null);
        this.cdr.markForCheck();
      });
  }

  private loadRelationData(): void {
    this.consumerService.ensureLoaded({ sort: 'name' });

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(this.subjectService.ensureLoaded(subjectQuery)).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery)),
      ),
    );
  }

  public ngAfterViewInit(): void {
    this.formGroup.markAllAsTouched();
  }

  public getConsumerError(): string {
    return getConsumerErrorText();
  }

  public getSubjectError(): string {
    return getSubjectErrorText();
  }

  public getExerciseSubKind(): string {
    return this.data?.config?.exerciseSubKind?.toString() || '';
  }

  public checkName(formControl: AbstractControl): string | null {
    return checkForNameErrors(formControl);
  }

  public consumerChanged(ev: string): void {
    if (ev) {
      this.consumerIsDeleted.set(false);
    }
  }

  public competencyChanged(ev: string): void {
    if (ev) {
      this.competencyIsDeleted.set(false);
    }
  }

  statusChanged(newStatus: string) {
    this.formGroup.get('approval')?.setValue(newStatus);
  }

  public getStateColor(): string {
    return getStatusColor(this.formGroup.value.approval);
  }

  public getStateText(): string {
    return getStatusText(this.formGroup.value.approval);
  }

  getConsumer(): ConsumerEntity {
    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    return this.consumerService.stateClass.byId(state)(this.data?.id);
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onSave(): void {
    // set IDs according to selected consumer/subject
    const name = this.formGroup.value.name as string;
    const subjectId = this.formGroup.value.subjects as string;
    const consumerId = this.formGroup.value.consumers as string;
    this.formGroup.patchValue({
      sortName: name.toLocaleLowerCase(),
      subjectId: subjectId,
      consumerId: consumerId,
    });

    if (!this.formGroup.valid) {
      this.widgetService.setMessage({
        message: this.translocoService.translate('required_error'),
        messageTye: 'mtError',
      });
      return;
    }

    const formValue = this.formGroup.value;
    const updatedExercise = Object.assign(
      new ExerciseEntity(),
      { id: this.data?.id },
      formValue,
      {
        consumers: formValue.consumers ? { id: formValue.consumers } : [],
        subjects: formValue.subjects ? { id: formValue.subjects } : [],
      },
    );

    this.dialogRef.close(updatedExercise);
  }

  public showFunctionalHelpDialog(): void {
    const functionalHelpJsonModel = new FunctionalHelpJsonModel({
      id: '',
      name: '',
    });

    const dialogRef = this.dialog.open(FunctionalHelpDialogComponent, {
      minWidth: '1000px',
      height: '70vh',
      data: functionalHelpJsonModel,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dialogData: FunctionalHelpJsonModel) => {
        if (dialogData) {
          console.log(`ID: ${dialogData.id}, Name: ${dialogData.name}`);
        }
      });
  }
}
